import { T, useTranslate } from '@tolgee/react';
import { Divider, Select, Tooltip } from 'antd';
import Modal from 'antd/lib/modal/Modal';
import { AxiosError } from 'axios';
import { CSSProperties, useEffect, useState } from 'react';
import styled from 'styled-components';

import { CrossSvg, QuestionMarkSvg } from '../../../assets/icons';
import LinkSvg from '../../../assets/icons/LinkSvg';
import theme from '../../../assets/theme';
import useAccessBadge, { LicenseCoarseLevels } from '../../../hooks/useAccessBadge';
import useInvitation from '../../../hooks/useInvitation';
import { useIsDesktop } from '../../../hooks/useIsDesktop';
import { newRelicNoticeError } from '../../../hooks/useNewRelic';
import {
  EmailInvitationCreateDTO,
  InvitationRole,
} from '../../../services/backendService/types';
import { Loader } from '../../_atoms/_Loader';
import Label from '../../_atoms/Label';
// The Members tab's own primitives, so the dialog it opens doesn't look like a
// different product. Same import direction ProfileSettingsPage already uses.
import {
  BtnOrange,
  C,
  DropdownOption,
  FieldLabel,
  FilterDropdown,
} from '../../dynamicPages/OrganizationSettingsPage/orgSettingsUi';
import { CenteredContainer } from '../../styled/CenteredContainer';
import { InvitationButton } from '../InvitationLink/InvitationLink';

const helpfulEmailRegex = new RegExp(
  `[a-zA-Z0-9.!#$%&amp;^_{}~-]+@[a-zA-Z0-9-]+(?:.[a-zA-Z0-9-]+)*`,
);

const containsInvalidEmail = (emails: string[]) =>
  emails.some(email => !helpfulEmailRegex.test(email));

// FilterDropdown's menu is `min-width: 100%` of its wrapper, and the wrapper is a
// plain block div — so left to itself in a dialog this wide, the trigger stays
// compact while the menu stretches the full width. Sizing the wrapper keeps the
// two aligned. Both option sets are short words, so this is ample.
const ROLE_DROPDOWN_WIDTH = 220;

const DURATION_OPTIONS: DropdownOption[] = [
  { value: '2', label: '2 hours' },
  { value: '12', label: '12 hours' },
  { value: String(7 * 24), label: '7 days' },
  { value: String(14 * 24), label: '14 days' },
  { value: '-1', label: 'Never' },
];

const InvitationLinkSettings = ({
  onDone,
  duration,
  role,
  allowAdminRole,
}: {
  duration?: number;
  role: InvitationRole;
  allowAdminRole?: boolean;
  onDone: (invitationDuration: number, role: InvitationRole) => void;
}) => {
  const [invitationDuration, setInvitationDuration] = useState(14 * 24);

  const [localRole, setRole] = useState(role);

  useEffect(() => {
    if (duration !== undefined) {
      setInvitationDuration(duration);
    }
  }, [duration]);
  useEffect(() => {
    if (role !== undefined) {
      setRole(role);
    }
  }, [role]);

  return (
    <>
      <h1>Invitation link settings</h1>
      <div style={{ width: '100%' }}>
        <FieldLabel>Role</FieldLabel>
        {/* Admins only. Kept in step with the picker in the main panel — both write
            the same role, so offering Admin in one and not the other would let the
            link and the email invites disagree. */}
        <FilterDropdown
          width={ROLE_DROPDOWN_WIDTH}
          value={String(localRole)}
          options={[
            { value: 'Member', label: 'Member' },
            ...(allowAdminRole ? [{ value: 'Admin', label: 'Admin' }] : []),
          ]}
          onChange={v => setRole(v as InvitationRole)}
        />
        <FieldLabel style={{ marginTop: 20 }}>Expires in</FieldLabel>
        {/* FilterDropdown speaks strings; the duration is hours as a number. */}
        <FilterDropdown
          width={ROLE_DROPDOWN_WIDTH}
          value={String(invitationDuration)}
          options={DURATION_OPTIONS}
          onChange={v => setInvitationDuration(Number(v))}
        />

        <div
          style={{
            marginTop: 10,
            display: 'flex',
            justifyContent: 'flex-end',
            width: '100%',
          }}
        >
          <BtnOrange onClick={() => onDone(invitationDuration, localRole)}>
            Generate new link
          </BtnOrange>
        </div>
      </div>
    </>
  );
};

function sanitizeEmail(email: string) {
  // When pasting from windows, a newline might look like: \r\n instead of \n. Antd doesnt handle this, and thus we need to handle it
  return email.replace('\r', '');
}

const InvitationModal = ({
  open,
  onDone,
  role,
  isSignup,
  title,
  allowAdminRole,
}: {
  open: boolean;
  onDone: () => void;
  role: InvitationRole;
  isSignup?: boolean;
  title?: string;
  // Off by default: the signup and top-bar invite flows must keep offering exactly
  // the role they were opened for.
  allowAdminRole?: boolean;
}) => {
  const { t } = useTranslate();
  return (
    <ModalStyled
      open={open}
      centered
      destroyOnClose
      footer={null}
      closable={false}
      bodyStyle={{ padding: 0 }}
      onCancel={() => onDone()}
      onOk={() => onDone()}
    >
      <ModalContainer>
        <CrossIconWrapper onClick={() => onDone()}>
          <CrossSvg />
        </CrossIconWrapper>
        <h1 style={{ width: '100%', textAlign: 'left' }}>{title || t('Invite')}</h1>
        <InviteToTenant
          onDone={onDone}
          role={role}
          isSignup={isSignup}
          hideValidity={false}
          allowAdminRole={allowAdminRole}
        />
      </ModalContainer>
    </ModalStyled>
  );
};

export const InviteToTenant = ({
  onDone,
  role,
  isSignup,
  hideValidity = false,
  showEmailInvite = true,
  allowAdminRole = false,
}: {
  onDone: () => void;
  role: InvitationRole;
  isSignup?: boolean;
  hideValidity?: boolean;
  style?: CSSProperties;
  showEmailInvite?: boolean;
  allowAdminRole?: boolean;
}) => {
  const {
    accessBadge: userAccessBadge,
    isFetching: isFetchingAccessBadge,
  } = useAccessBadge();
  const isDesktop = useIsDesktop();
  const [val, setVal] = useState<string[]>([]);
  const { t } = useTranslate();
  const [invitationDuration, setInvitationDuration] = useState(14 * 24);
  const [isEditingInvitation, setIsEditingDuration] = useState(false);
  const [localRole, setLocalRole] = useState(role);
  useEffect(() => {
    setLocalRole(role);
  }, [role]);

  const canInvite = !!(
    (userAccessBadge &&
      userAccessBadge.license.coarseLevel !== LicenseCoarseLevels.premium) ||
    (userAccessBadge &&
      userAccessBadge.license.coarseLevel === LicenseCoarseLevels.premium &&
      userAccessBadge.role === 'Admin')
  );

  const isPremium =
    userAccessBadge &&
    userAccessBadge.license.coarseLevel === LicenseCoarseLevels.premium;

  const isLoadingCanInvite = isFetchingAccessBadge;

  // Two conditions, both required: the call site opted in, and the person doing the
  // inviting is themselves an Admin. Checking the badge here as well as at the call
  // site means a future caller can't hand out Admin by passing a flag.
  const canOfferAdminRole = allowAdminRole && userAccessBadge?.role === 'Admin';

  const {
    url,
    bulkInvite,
    error: inviteError,
    isLoading,
    isSuccess,
    isFetching,
  } = useInvitation(localRole, undefined, invitationDuration);
  const handleSend = () => {
    if (val.length === 0) {
      setError(t('Please enter one or more email addresses to invite new users'));
      return;
    }
    if (containsInvalidEmail(val)) {
      setError(t('Atleast one of the provided emails are not valid'));
      return;
    }
    const payloads = val.map(email => {
      const p: EmailInvitationCreateDTO = {
        email: sanitizeEmail(email),
        role: localRole,
      };
      return p;
    });
    bulkInvite(payloads);
  };

  useEffect(() => {
    if (inviteError) {
      newRelicNoticeError(inviteError, 'Error while sending invitation');

      const axiosError = inviteError as AxiosError<{ error?: { message?: string } }>;
      const apiMessage = axiosError?.response?.data?.error?.message;
      setError(apiMessage || t('An error occurred, please try again'));
    }
  }, [inviteError, t]);

  useEffect(() => {
    if (isSuccess) {
      onDone();
    }
  }, [isSuccess, onDone]);

  const handleChange = (e: string[]) => {
    const hey = new Set([...e]);
    const yo = Array.from(hey);
    setVal(yo);
  };
  const CustomTagRenderer = (props: {
    value: string;
    closable: boolean;
    onClose: () => void;
  }) => {
    const { value, closable, onClose } = props;

    const isValidEmail = helpfulEmailRegex.test(value);
    return (
      <Label
        backgroundColor={isValidEmail ? theme.colors.green : theme.colors.red}
        color={theme.colors.white}
        text={value}
        closable={closable}
        onClose={onClose}
        style={{
          fontSize: 13,
          fontWeight: 'bold',
          marginRight: 5,
          marginTop: 2,
          marginBottom: 2,
        }}
      />
    );
  };

  const [error, setError] = useState<string>();
  if (isLoadingCanInvite) {
    return (
      <div style={{ width: '100%' }}>
        <CenteredContainer>
          <Loader />
        </CenteredContainer>
      </div>
    );
  }

  if (!canInvite) {
    return (
      <div style={{ width: '100%' }}>
        <CenteredContainer>
          <p>{t('Only the admin of this tenant can invite other members')}</p>
        </CenteredContainer>
      </div>
    );
  }

  return (
    <div style={{ width: '100%' }}>
      {isPremium && (
        <p style={{ fontStyle: 'italic', fontWeight: 'bold' }}>
          {t('Note: A new team member counts as one seat for your next billing cycle')}
        </p>
      )}
      {isEditingInvitation && (
        <InvitationLinkSettings
          role={localRole}
          allowAdminRole={canOfferAdminRole}
          onDone={(duration, role) => {
            setInvitationDuration(duration);
            setLocalRole(role);
            setIsEditingDuration(false);
          }}
        />
      )}
      {!isEditingInvitation && (
        <>
          {canOfferAdminRole && (
            <div style={{ width: '100%', marginBottom: 20 }}>
              <FieldLabel>{t('Role')}</FieldLabel>
              <FilterDropdown
                width={ROLE_DROPDOWN_WIDTH}
                value={String(localRole)}
                options={[
                  { value: 'Member', label: t('Member') },
                  { value: 'Admin', label: t('Admin') },
                ]}
                onChange={v => setLocalRole(v as InvitationRole)}
              />
              <p style={{ fontSize: 14, marginTop: 8, marginBottom: 0 }}>
                {t(
                  'inviteRoleHint',
                  'Applies to both the emails and the invite link below. Admins can manage members, students and organization settings.',
                )}
              </p>
            </div>
          )}
          {showEmailInvite && (
            <div style={{ width: '100%' }}>
              <p style={{ fontSize: 16, fontWeight: 'bold' }}>{t('Emails')}</p>
              <SelectStyled
                mode="tags"
                placeholder={t(
                  'youCanCopy',
                  'You can copy and paste a list of emails...',
                )}
                tokenSeparators={[',', ' ', '\r\n', '\n', '\t']}
                style={{ width: '100%', maxHeight: '250px', overflowY: 'auto' }}
                value={val}
                onChange={e => handleChange(e as string[])}
                tagRender={({ closable, onClose, value }) => (
                  <CustomTagRenderer
                    closable={closable}
                    onClose={onClose}
                    value={value as string}
                  />
                )}
                dropdownRender={() => <></>}
                dropdownStyle={{ display: 'none' }}
              />
              <p
                style={{
                  fontSize: 14,
                  fontWeight: 'normal',
                  marginTop: 15,
                  marginBottom: 15,
                }}
              >
                {t(
                  'weWillSendAn',
                  'We will send an email invitation with a link to each of the emails above.',
                )}
              </p>
              <HorizontalWrapper
                style={{ justifyContent: 'space-between', marginTop: 10 }}
              >
                <BtnOrange onClick={handleSend} disabled={isLoading}>
                  {isLoading
                    ? t('Sending…', 'Sending…')
                    : isDesktop
                    ? t('Send invitations')
                    : t('Send')}
                </BtnOrange>
                {error && (
                  <p
                    style={{
                      flex: 1,
                      fontSize: 14,
                      fontWeight: 'normal',
                      color: theme.colors.red,
                      padding: '0px 30px',
                      marginBottom: 0,
                    }}
                  >
                    {error}
                  </p>
                )}
              </HorizontalWrapper>
            </div>
          )}
          {showEmailInvite && (
            <Divider style={{ borderTop: `2px solid #d9d9d9` }} type="horizontal" />
          )}
          {localRole && (
            <div
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
              }}
            >
              <HorizontalWrapper>
                <IconWrapper $height={22} $width={22}>
                  <LinkSvg />
                </IconWrapper>
                <ResponsiveInviteLinkText>{t('Invite link')}</ResponsiveInviteLinkText>
                <Tooltip
                  title={t('People with this link can join this tenant')}
                  trigger={isDesktop ? 'hover' : 'click'}
                  zIndex={99999}
                >
                  <IconWrapper $height={16} $width={16}>
                    <QuestionMarkSvg />
                  </IconWrapper>
                </Tooltip>
              </HorizontalWrapper>

              <InvitationButton isFetching={isFetching} isSignup={isSignup} url={url} />
            </div>
          )}
          {!hideValidity && (
            <ValidText>
              {invitationDuration === -1 && t('You invitation link will never expire.')}
              {invitationDuration !== -1 && invitationDuration > 24 && (
                <T
                  keyName="Your invitation link expires in {numberOfDays} days"
                  defaultValue="Your invitation link expires in {numberOfDays} days"
                  params={{ numberOfDays: invitationDuration / 24 }}
                />
              )}
              {invitationDuration !== -1 && invitationDuration <= 24 && (
                <T
                  keyName="Your invitation link expires in {numberOfHours} hours"
                  defaultValue="Your invitation link expires in {numberOfHours} hours"
                  params={{ numberOfHours: invitationDuration }}
                />
              )}
              <HoverableTextButton
                type="button"
                onClick={() => setIsEditingDuration(true)}
              >
                <T keyName="Edit invitation link" defaultValue="Edit invitation link" />
              </HoverableTextButton>
            </ValidText>
          )}
        </>
      )}
    </div>
  );
};

const ResponsiveInviteLinkText = styled.p`
  margin-left: 10px;
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 0px;
  white-space: nowrap;
  flex: 0;
  margin-right: 3px;
  @media (max-width: 500px) {
    display: none;
  }
`;

const HoverableTextButton = styled.button`
  /* Was a span, so it was unreachable by keyboard and screen readers announced
     nothing actionable. A button element gets both for free; the reset below
     keeps the inline-text look it had. */
  border: none;
  background: none;
  padding: 0;
  font: inherit;
  /* The expiry sentence runs straight into this without it. */
  margin-left: 6px;
  color: ${C.orangeDark};
  font-weight: 800;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.645, 0.045, 0.355, 1);
  &:hover {
    text-decoration: underline;
  }
  &:focus-visible {
    outline: 2px solid ${C.orange};
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

const CrossIconWrapper = styled.div`
  color: ${theme.colors.black};
  transition: all 0.2s cubic-bezier(0.645, 0.045, 0.355, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  svg {
    height: 20px;
    width: 20px;
  }
  cursor: pointer;
  border-radius: 50px;
  padding: 5px;
  &:hover {
    background: rgba(0, 0, 0, 0.3);
  }
  top: 20px;
  right: 20px;
  position: absolute;
`;

const IconWrapper = styled.div<{ $height: number; $width: number }>`
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${theme.colors.black};
  svg {
    height: ${({ $height }) => `${$height}px`};
    width: ${({ $width }) => `${$width}px`};
  }
`;

const HorizontalWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const ValidText = styled.p`
  margin-bottom: 0px;
  width: 100%;
  text-align: left;
  margin-top: 10px;
  font-size: 12px;
  color: ${theme.colors.text.darkGrey};
  font-weight: bold;
`;

const ModalContainer = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  border-radius: 10px;
  h1 {
    font-weight: bold;
    text-align: center;
    margin-bottom: 20px;
  }
`;

const ModalStyled = styled(Modal)`
  .ant-modal-content {
    border-radius: 10px;
    box-shadow: none;
  }
  .ant-modal-body {
    box-shadow: none;
    border-radius: 10px;
    background-color: ${theme.colors.background};
  }
`;

// The emails field. Stays an antd Select because it is a multi-tag input, but its
// border, radius and focus ring are FieldInput's so it matches the Members tab.
// The per-email pills keep their validity colours (CustomTagRenderer paints them
// green or red) — that is a valid/invalid signal, not branding.
export const SelectStyled = styled(Select)`
  && .ant-select-selector {
    border: 1.5px solid ${C.grey};
    border-radius: 10px !important;
    color: ${C.ink};
    font-family: inherit;
    font-weight: 700;
    font-size: 16px;
    line-height: 130%;
    padding: 10px;
    min-height: 60px;
    transition: all 0.2s ease;
  }
  &&.ant-select-focused .ant-select-selector,
  && .ant-select-selector:hover {
    border-color: ${C.orange} !important;
    box-shadow: 0 0 0 3px ${C.orangeTint} !important;
  }
  && .ant-select-selection-search {
    margin-inline-start: 0px !important;
  }
  && .ant-select-selection-placeholder {
    color: ${C.fg3};
    font-weight: 700;
  }
  && .ant-select-selection-item {
    border-radius: 20px;
    padding: 0 10px;
    display: flex;
    align-items: center;
  }
  && .ant-select-selection-item-remove {
    color: white;
  }
`;

export default InvitationModal;
